import type {
	AiManagedObjectSourceApp,
	AiManagedObjectSourceInput,
} from '@atlassian/ai-managed-objects/types';

import type { ManagedBlockSource, ManagedBlockSourceMode } from './types';

export const DEFAULT_SOURCE_MODE: ManagedBlockSourceMode = 'current-page';
export const MAX_MANAGED_BLOCK_SOURCES = 10;

const normalizeUrl = (url: string): string => url.trim();

export const isValidSourceUrl = (url: string): boolean => {
	try {
		new URL(url);
		return true;
	} catch {
		return false;
	}
};

const matchesHostname = (hostname: string, domain: string): boolean =>
	hostname === domain || hostname.endsWith(`.${domain}`);

const isAtlassianHost = (hostname: string): boolean =>
	matchesHostname(hostname, 'atlassian.net') ||
	matchesHostname(hostname, 'atlassian.com') ||
	matchesHostname(hostname, 'jira-dev.com');

export const getSourceAppFromUrl = (url: string): AiManagedObjectSourceApp => {
	try {
		const { hostname, pathname } = new URL(url);
		const lowerHostname = hostname.toLowerCase();
		const lowerPathname = pathname.toLowerCase();

		// TODO: Derive source app from ORS when Smart Link resolution is supported.
		if (isAtlassianHost(lowerHostname)) {
			return lowerPathname.startsWith('/wiki') ? 'CONFLUENCE' : 'JIRA';
		}

		if (matchesHostname(lowerHostname, 'bitbucket.org')) {
			return 'BITBUCKET';
		}

		if (matchesHostname(lowerHostname, 'github.com')) {
			return 'GITHUB';
		}

		if (
			matchesHostname(lowerHostname, 'drive.google.com') ||
			matchesHostname(lowerHostname, 'docs.google.com')
		) {
			return 'GOOGLE_DRIVE';
		}

		if (matchesHostname(lowerHostname, 'loom.com')) {
			return 'LOOM';
		}

		if (matchesHostname(lowerHostname, 'slack.com')) {
			return 'SLACK';
		}

		return 'WEB';
	} catch {
		return 'UNKNOWN';
	}
};

export const getSourceUrlCandidatesFromText = (sourceUrls: string): string[] => {
	const seenUrls = new Set<string>();

	return sourceUrls
		.split(/\s+/)
		.map(normalizeUrl)
		.filter((url) => url.length > 0)
		.filter((url) => {
			if (seenUrls.has(url)) {
				return false;
			}

			seenUrls.add(url);
			return true;
		});
};

export const getSourceUrlsFromText = (sourceUrls: string): string[] =>
	getSourceUrlCandidatesFromText(sourceUrls).filter(isValidSourceUrl);

const URL_CANDIDATE_PATTERN = /https?:\/\/[^\s<>"']+/gi;
const TRAILING_URL_PUNCTUATION_PATTERN = /[.,;:!?)}\]]+$/;

export const getSourceUrlCandidatesFromPromptText = (promptText: string): string[] => {
	const seenUrls = new Set<string>();
	const matches = promptText.match(URL_CANDIDATE_PATTERN) ?? [];

	return matches
		.map((url) => normalizeUrl(url).replace(TRAILING_URL_PUNCTUATION_PATTERN, ''))
		.filter((url) => url.length > 0 && isValidSourceUrl(url))
		.filter((url) => {
			if (seenUrls.has(url)) {
				return false;
			}

			seenUrls.add(url);
			return true;
		});
};

export const getSourcesFromText = (sourceUrls: string): AiManagedObjectSourceInput[] =>
	getSourceUrlsFromText(sourceUrls).map((url) => ({
		app: getSourceAppFromUrl(url),
		url,
	}));

export const getSourceUrlsFromEntries = (sourceEntries: ManagedBlockSource[]): string =>
	sourceEntries.map((source) => source.url).join('\n');

export const getResolvedSourcesFromEntries = (
	sourceEntries: ManagedBlockSource[],
): AiManagedObjectSourceInput[] =>
	sourceEntries
		.filter(
			(source): source is ManagedBlockSource & Required<Pick<ManagedBlockSource, 'app'>> =>
				source.status === 'resolved' && Boolean(source.app),
		)
		.map((source) => ({
			app: source.app,
			url: source.url,
		}));

export const getSourceEntriesFromSources = (
	sources?: AiManagedObjectSourceInput[] | null,
): ManagedBlockSource[] =>
	sources?.map((source) => ({
		app: source.app,
		status: 'resolving',
		url: source.url,
	})) ?? [];

export const getCurrentPageSource = (
	currentPageSourceUrl?: string,
): AiManagedObjectSourceInput[] => {
	const url = normalizeUrl(currentPageSourceUrl ?? '');

	return url.length > 0 && isValidSourceUrl(url)
		? [
				{
					app: 'CONFLUENCE',
					url,
				},
			]
		: [];
};
